// NA1-nav-loading — route-pattern → skeleton-template mapping (nav-loading
// study doctrine §4: the route boundary's fallback is the DESTINATION's kit
// template, matched by pattern; default ListPageSkeleton). Lives here (not
// routes.tsx) because AppShell consumes it and routes.tsx imports AppShell —
// exporting from routes.tsx would create an import cycle.
import type { ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router';
import {
  DashboardSkeleton,
  FormPageSkeleton,
  ListPageSkeleton,
  ManageShellSkeleton,
  SettingsShellSkeleton,
  ShowPageSkeleton,
  TakeShellSkeleton,
} from './RouteSkeletons';

export type RouteSkeletonFamily = 'settings-shell' | 'dashboard' | 'show' | 'form' | 'list';

const SETTINGS_PATTERNS = [
  '/settings',
  '/settings/*',
  '/orgs/:orgId/settings',
  '/orgs/:orgId/settings/*',
];

const DASHBOARD_PATTERNS = ['/ai-usage', '/students/:uuid'];

const SHOW_PATTERNS = [
  '/groups/show/:id',
  '/rooms/show/:id',
  '/standard-sets/:id',
  '/standard-sets/:standardCollectionId/content-areas/:id',
  '/curriculum/:id',
  '/profile',
];

const matchesAny = (patterns: string[], pathname: string) =>
  patterns.some((pattern) => matchPath({ path: pattern, end: true }, pathname));

export const resolveSkeletonFamily = (pathname: string): RouteSkeletonFamily => {
  if (matchesAny(SETTINGS_PATTERNS, pathname)) return 'settings-shell';
  if (matchesAny(DASHBOARD_PATTERNS, pathname)) return 'dashboard';
  if (matchesAny(SHOW_PATTERNS, pathname)) return 'show';
  // Every CRUD form route in the shell ends /create or /edit/:id.
  if (/\/create$|\/edit\/[^/]+$/.test(pathname)) return 'form';
  return 'list';
};

/** v3 ExperienceManage.getActiveKeyFromPath — manage-tab detection for the
 *  /assessments/:id/* deep routes. Lives HERE (R2-G62) so Routes.tsx and the
 *  blocking providers share one copy: importing it from @/pages/experiences
 *  would pull the lazy manage chunk into the main bundle. */
export const getManageTabFromPath = (pathname: string): string | null => {
  for (const tab of ['create', 'deliver', 'monitor', 'grade', 'analytics']) {
    if (pathname.includes(`/${tab}/`) || pathname.endsWith(`/${tab}`)) return tab;
  }
  return null;
};

/** R2-G62 — full-screen shell skeleton for the experience deep routes
 *  (manage vs take by path); null anywhere else, so callers chain
 *  `?? <their own fallback>`. One loading language: the auth check, the
 *  route chunk load, and the blocking providers all show the same shell. */
export const resolveExperienceShellSkeleton = (pathname: string): ReactNode | null => {
  if (!matchPath('/assessments/:id/*', pathname)) return null;
  return getManageTabFromPath(pathname) ? <ManageShellSkeleton /> : <TakeShellSkeleton />;
};

/** Suspense fallback for the in-shell route boundary: renders the destination
 *  page family's shaped skeleton under persistent chrome. */
export const RouteFallback = () => {
  const { pathname } = useLocation();
  switch (resolveSkeletonFamily(pathname)) {
    case 'settings-shell':
      return <SettingsShellSkeleton />;
    case 'dashboard':
      return <DashboardSkeleton />;
    case 'show':
      return <ShowPageSkeleton />;
    case 'form':
      return <FormPageSkeleton />;
    default:
      return <ListPageSkeleton />;
  }
};
